// Fiches employés (personnel suivi au pointage — sans compte de connexion).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid,
  IconButton, MenuItem, Snackbar, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import { AddOutlined, EditOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { rhAPI } from '../../services/api';
import { apiError, dateFr } from '../../utils/format';
import { CONTRATS } from '../../utils/rh';
import { tokens } from '../../theme';

const EMPTY = {
  matricule: '', nom: '', prenom: '', poste: '', service: '', telephone: '', type_contrat: 'CDI', date_embauche: '',
  date_sortie: '', badge: '', heure_debut: '', heure_fin: '', conges_report: 0, is_active: true,
};

const RhEmployes = () => {
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [f, setF] = useState(null);
  const [dlgError, setDlgError] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    rhAPI.employes(showAll).then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  }, [showAll]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setDlgError('');
    try {
      const data = { ...f };
      Object.keys(data).forEach((k) => { if (data[k] === null) data[k] = ''; });
      if (f.id) await rhAPI.updateEmploye(f.id, data); else await rhAPI.createEmploye(data);
      setToast(f.id ? 'Fiche modifiée' : 'Employé ajouté'); setF(null); load();
    } catch (e) { setDlgError(apiError(e)); }
  };

  const q = search.trim().toLowerCase();
  const list = rows.filter((e) => !q || `${e.matricule} ${e.nom} ${e.prenom} ${e.poste || ''} ${e.service || ''}`.toLowerCase().includes(q));
  const field = (key, label, props = {}) => (
    <TextField fullWidth size="small" label={label} value={f[key] ?? ''} onChange={(e) => setF({ ...f, [key]: e.target.value })} {...props} />
  );

  return (
    <Layout title="Employés" plain
      actions={<Button variant="contained" startIcon={<AddOutlined />} onClick={() => { setDlgError(''); setF({ ...EMPTY }); }}>Nouvel employé</Button>}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Rechercher" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="Afficher les anciens employés" />
        <Box sx={{ ml: 'auto', color: 'text.secondary', fontSize: 14 }}>{list.length} employé(s)</Box>
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Matricule</TableCell><TableCell>Nom et prénom</TableCell><TableCell>Poste</TableCell><TableCell>Service</TableCell>
                <TableCell>Contrat</TableCell><TableCell>Embauche</TableCell><TableCell>Badge</TableCell><TableCell>Horaire</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((e) => (
                <TableRow key={e.id} hover sx={{ opacity: e.is_active ? 1 : 0.55 }}>
                  <TableCell sx={{ fontFamily: tokens.mono }}>{e.matricule}</TableCell>
                  <TableCell><b>{e.nom}</b> {e.prenom}{!e.is_active && <Chip size="small" label="Sorti" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{e.poste}</TableCell>
                  <TableCell>{e.service}</TableCell>
                  <TableCell>{CONTRATS[e.type_contrat]}</TableCell>
                  <TableCell>{e.date_embauche ? dateFr(e.date_embauche) : ''}</TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono }}>{e.badge}</TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono, fontSize: 12.5 }}>{e.heure_debut ? `${e.heure_debut}–${e.heure_fin}` : <span style={{ color: '#9AA3AF' }}>général</span>}</TableCell>
                  <TableCell padding="checkbox">
                    <IconButton size="small" aria-label={`Modifier ${e.nom} ${e.prenom}`} onClick={() => { setDlgError(''); setF({ ...EMPTY, ...e }); }}><EditOutlined fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>Aucun employé</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!f} onClose={() => setF(null)} fullWidth maxWidth="sm">
        {f && (
          <>
            <DialogTitle>{f.id ? `${f.nom} ${f.prenom}` : 'Nouvel employé'}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={4}>{field('matricule', 'Matricule', { required: true })}</Grid>
                <Grid item xs={12} sm={4}>{field('nom', 'Nom', { required: true })}</Grid>
                <Grid item xs={12} sm={4}>{field('prenom', 'Prénom', { required: true })}</Grid>
                <Grid item xs={12} sm={6}>{field('poste', 'Poste (ex. Ferrailleur, Soudeur)')}</Grid>
                <Grid item xs={12} sm={6}>{field('service', 'Service (ex. Production)')}</Grid>
                <Grid item xs={12} sm={6}>{field('telephone', 'Téléphone')}</Grid>
                <Grid item xs={12} sm={6}>
                  {field('type_contrat', 'Contrat', { select: true, children: Object.entries(CONTRATS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>) })}
                </Grid>
                <Grid item xs={12} sm={6}>{field('date_embauche', "Date d'embauche", { type: 'date', InputLabelProps: { shrink: true } })}</Grid>
                <Grid item xs={12} sm={6}>{field('date_sortie', 'Date de sortie', { type: 'date', InputLabelProps: { shrink: true } })}</Grid>
                <Grid item xs={12} sm={6}>{field('badge', 'N° badge pointeuse', { helperText: "Identifiant de l'employé sur la pointeuse" })}</Grid>
                <Grid item xs={12} sm={6}>{field('conges_report', 'Congés reportés (jours)', { inputMode: 'decimal', helperText: "Reliquat de l'année précédente" })}</Grid>
                <Grid item xs={6} sm={3}>{field('heure_debut', 'Début propre', { type: 'time', InputLabelProps: { shrink: true } })}</Grid>
                <Grid item xs={6} sm={3}>{field('heure_fin', 'Fin propre', { type: 'time', InputLabelProps: { shrink: true } })}</Grid>
                <Grid item xs={12} sm={6} sx={{ fontSize: 12.5, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  Laisser vide pour appliquer l'horaire général (Paramètres RH).
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />}
                    label="Employé en activité (décocher après son départ)" />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setF(null)}>Annuler</Button>
              <Button variant="contained" onClick={save}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default RhEmployes;
